import React, { SyntheticEvent, useState } from 'react';
import {
  CircularProgress,
  Autocomplete,
  debounce,
  TextField,
} from '@mui/material';
import { IPostcode } from '../../Interfaces/IPostcode';
import { useSearchPostcodes } from '../../hooks/reactQuerys/PostcodeQueries';

interface IProps {
  postcodeObject: IPostcode | null | undefined;
  updatePostcodeObject: (value: IPostcode | null | undefined) => void;
  required?: boolean;
}

export const PostcodeSearch = ({
  postcodeObject,
  updatePostcodeObject,
  required,
}: IProps) => {
  const [open, setOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const { postcodes, isLoadingPostcodes, abortSearchPostcodes } =
    useSearchPostcodes(debouncedSearchTerm);

  const options = postcodes ?? [];

  const handleSearchTermChange = (_: SyntheticEvent, searchTerm: string) => {
    abortSearchPostcodes();
    setSearchTerm(searchTerm);
    const debouncedSearchTerm = debounce(setDebouncedSearchTerm, 500);

    debouncedSearchTerm(searchTerm);
  };

  return (
    <Autocomplete
      id='searchbar-postcode'
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={postcodeObject}
      inputValue={searchTerm}
      isOptionEqualToValue={(option, value) =>
        option.idPostcode === value.idPostcode
      }
      filterOptions={x => x}
      getOptionLabel={option => `${option.Postcode} ${option.City}`}
      loadingText='Laden...'
      options={options}
      loading={isLoadingPostcodes}
      getOptionKey={option =>
        `${option.idPostcode}-${option.idCountry}-${option.City}`
      }
      renderOption={(props, option) => (
        <li {...props}>
          <span>
            {option.Postcode} {option.City}
            {option.Municipality ? (
              <span
                style={{ color: '#9aa3af', marginLeft: 6, fontSize: '0.85em' }}
              >
                · {option.Municipality}
              </span>
            ) : null}
          </span>
        </li>
      )}
      onInputChange={handleSearchTermChange}
      onChange={(_, newObject) => updatePostcodeObject(newObject)}
      renderInput={params => (
        <TextField
          {...params}
          label='Postleitzahl'
          placeholder='Postleitzahl eingeben'
          size='small'
          required={required ?? true}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoadingPostcodes && (
                  <CircularProgress color='inherit' size={20} />
                )}
                {searchTerm && params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};
